// @file School permissions Teachers settings store
import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import {
  createRetroactiveWallUpdatesConfirmationDialog,
  willTriggerRetroactiveWallUpdatesV2,
} from '@@/bits/permissions_settings_helper'
import PromiseQueue from '@@/bits/promise_queue'
import { LibraryPermissions as LibraryPermissionsApi } from '@@/dashboard/padlet_api'
import type { WallAccessDefault } from '@@/enums'
import {
  AllowedDefaultWallRole,
  LibraryType,
  SchoolPermissionString,
  SchoolTeacherPermissionBoolean,
  SnackbarNotificationType,
  WallAccessRule,
  WallPermission,
  WallPrivacyOption,
} from '@@/enums'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Library, SchoolLibraryPermission, SchoolLibraryTeacherPermission } from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref, shallowReactive } from 'vue'

enum SchoolPermissionsTeachersStatus {
  Loading = 'Loading',
  Completed = 'Completed',
  Errored = 'Errored',
}

const useSettingsSchoolPermissionsTeachersStore = defineStore('settingsSchoolPermissionsTeachers', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const queue = new PromiseQueue()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
  // State
  const permissionsState = shallowReactive<SchoolLibraryTeacherPermission>({
    canMakeWalls: false,
    canMakePrivateWalls: false,
    canMakeLibraryWideHiddenWalls: false,
    canMakeLibraryWideDashboardWalls: false,
    canMakePasswordWalls: false,
    canMakeSecretLoggedInVisitorsWalls: false,
    canMakeSecretAllVisitorsWalls: false,
    canMakePublicWalls: false,
    defaultWallPrivacy: WallPrivacyOption.LibraryWideUnlisted,
    defaultWallVisitorPermission: WallPermission.Write,
    canInviteNewStudents: false,
    canInviteNewTeachers: false,
    canAccessNonSchoolLibraries: false,
    canAccessWallsFromOutsideSchool: false,
    canInviteCollaboratorsFromOutsideSchool: false,
    canTransferWalls: false,
    canUseAiImageAttachments: true,
    canUseAiSpeechAttachments: true,
    canUseAiRecipes: true,
    canUsePadletIntelligence: true,
    canUseVirtualFieldTrip: true,
    listedSetting: WallAccessRule.Allowed,
    publishableSetting: WallAccessRule.Allowed,
    requireLogin: WallAccessRule.Allowed,
    requirePassword: WallAccessRule.Allowed,
    allowVisitorAccess: WallAccessRule.Allowed,
    allowStudentAccess: WallAccessRule.Allowed,
    allowTeacherAccess: WallAccessRule.Allowed,
    defaultWallVisitorRole: AllowedDefaultWallRole.Commenter,
    defaultWallStudentRole: AllowedDefaultWallRole.Commenter,
    defaultWallTeacherRole: AllowedDefaultWallRole.Commenter,
  })

  const schoolPermissionsTeachersStatus = ref(SchoolPermissionsTeachersStatus.Loading)
  const library = ref<Library>()
  const defaultPrivacy = ref<WallPrivacyOption>(WallPrivacyOption.Private)

  // Getters
  const isSchoolLibrary = computed(() => library.value?.libraryType === LibraryType.School)
  const isSchoolPermissionsTeachersLoading = computed(
    () => schoolPermissionsTeachersStatus.value === SchoolPermissionsTeachersStatus.Loading,
  )

  const disabledTeacherDefaultPrivacy = computed(() => {
    if (defaultPrivacy.value === WallPrivacyOption.Private && !permissionsState.canMakePrivateWalls) return true

    if (
      defaultPrivacy.value === WallPrivacyOption.LibraryWideUnlisted &&
      !permissionsState.canMakeLibraryWideHiddenWalls
    )
      return true

    if (
      defaultPrivacy.value === WallPrivacyOption.LibraryWideListed &&
      !permissionsState.canMakeLibraryWideDashboardWalls
    )
      return true

    if (defaultPrivacy.value === WallPrivacyOption.PasswordProtected && !permissionsState.canMakePasswordWalls)
      return true

    if (
      defaultPrivacy.value === WallPrivacyOption.LoggedInUsersOnly &&
      !permissionsState.canMakeSecretLoggedInVisitorsWalls
    )
      return true

    if (defaultPrivacy.value === WallPrivacyOption.Secret && !permissionsState.canMakeSecretAllVisitorsWalls)
      return true

    if (defaultPrivacy.value === WallPrivacyOption.Public && !permissionsState.canMakePublicWalls) return true

    return false
  })

  // Spec for default privacy reset ordering: https://whimsical.com/permissions-1-0-Q6yc3eBEK2a8XBFsMzKNfg
  // if the default privacy is disabled, attempt to set the default to an enabled privacy option in the following order
  // this is not a descending order of security but of what privacy options we think are most preferable by users
  const mostSecureEnabledTeacherPrivacyOption = computed(() => {
    if (permissionsState.canMakeLibraryWideHiddenWalls) return WallPrivacyOption.LibraryWideUnlisted
    if (permissionsState.canMakeLibraryWideDashboardWalls) return WallPrivacyOption.LibraryWideListed
    if (permissionsState.canMakeSecretLoggedInVisitorsWalls) return WallPrivacyOption.LoggedInUsersOnly
    if (permissionsState.canMakeSecretAllVisitorsWalls) return WallPrivacyOption.Secret
    if (permissionsState.canMakePasswordWalls) return WallPrivacyOption.PasswordProtected
    if (permissionsState.canMakePrivateWalls) return WallPrivacyOption.Private
    if (permissionsState.canMakePublicWalls) return WallPrivacyOption.Public
    return WallPrivacyOption.LibraryWideUnlisted
  })
  const isMakeAPadletForTeachersDisabled = computed(() => !permissionsState.canMakeWalls)
  const areAllTeacherPrivacyOptionsDisabled = computed(
    () =>
      !permissionsState.canMakePrivateWalls &&
      !permissionsState.canMakeLibraryWideHiddenWalls &&
      !permissionsState.canMakeLibraryWideDashboardWalls &&
      !permissionsState.canMakePasswordWalls &&
      !permissionsState.canMakeSecretLoggedInVisitorsWalls &&
      !permissionsState.canMakeSecretAllVisitorsWalls &&
      !permissionsState.canMakePublicWalls,
  )

  // Actions
  function setPermissionsAndDefaultPrivacyState(permissions: SchoolLibraryTeacherPermission): void {
    defaultPrivacy.value = (permissions?.defaultWallPrivacy as WallPrivacyOption) ?? WallPrivacyOption.Private
    for (const permission in permissionsState) {
      permissionsState[permission] = permissions[permission] ?? false
    }
  }
  async function initialize(libraryObject?: Library): Promise<void> {
    if (libraryObject === undefined) {
      void globalSnackbarStore.genericFetchError()
      return
    }
    library.value = libraryObject
    await fetchLibraryPermissionsTeachers(libraryObject.id)
  }

  async function fetchLibraryPermissionsTeachers(libraryId: number): Promise<void> {
    try {
      schoolPermissionsTeachersStatus.value = SchoolPermissionsTeachersStatus.Loading

      const response = await LibraryPermissionsApi.fetch(libraryId)
      const permissions = (response.data as JsonAPIResource<SchoolLibraryPermission>)?.attributes.settings?.teacher

      if (permissions != null) setPermissionsAndDefaultPrivacyState(permissions)
      schoolPermissionsTeachersStatus.value = SchoolPermissionsTeachersStatus.Completed
    } catch (e) {
      void globalSnackbarStore.genericFetchError()
      schoolPermissionsTeachersStatus.value = SchoolPermissionsTeachersStatus.Errored
      captureFetchException(e, { source: 'SettingsSchoolPermissionsTeachersFetchPermissions' })
    }
  }

  async function updatePermissions(
    teacherPermission: SchoolTeacherPermissionBoolean,
    permissionValue: boolean,
    initialDefaultPrivacy: WallPrivacyOption,
  ): Promise<void> {
    const libraryId = library.value?.id as number
    try {
      const response = await LibraryPermissionsApi.update(libraryId, {
        teacher: { [teacherPermission]: permissionValue },
      })
      const permissions = (response.data as JsonAPIResource<SchoolLibraryPermission>)?.attributes.settings?.teacher
      if (permissions != null) setPermissionsAndDefaultPrivacyState(permissions)
    } catch (e) {
      queue.clear()
      void globalSnackbarStore.setSnackbar({
        message: __('Error updating permissions.'),
        notificationType: SnackbarNotificationType.error,
      })
      permissionsState[teacherPermission] = !permissionValue
      defaultPrivacy.value = initialDefaultPrivacy
      captureFetchException(e, { source: 'SettingsSchoolPermissionsTeachersUpdatePermissions' })
    }
  }

  async function queueUpdatePermissions(teacherPermission: SchoolTeacherPermissionBoolean): Promise<void> {
    // The queued update sends this value, because an earlier response can reset `permissionsState` first.
    const permissionValue = !permissionsState[teacherPermission]
    permissionsState[teacherPermission] = permissionValue
    const initialDefaultPrivacy = defaultPrivacy.value
    if (disabledTeacherDefaultPrivacy.value) {
      defaultPrivacy.value = mostSecureEnabledTeacherPrivacyOption.value
    }
    void queue.enqueue(
      'updatePermissions',
      async () => await updatePermissions(teacherPermission, permissionValue, initialDefaultPrivacy),
    )
  }

  function confirmDisableCanAccessNonSchoolLibraries(): void {
    globalConfirmationDialogStore.openConfirmationDialog({
      ...ALERT_ICON,
      title: __('Are you sure you want to remove access to personal workspaces?'),
      body: __('Your teachers will not be able to find or transfer padlets from their personal workspaces.', {
        libraryName: library.value?.name,
      }),
      forceFullWidthButtons: true,
      confirmButtonText: __('Disable'),
      cancelButtonText: __('Nevermind'),
      afterConfirmCallbacks: [
        async () => await queueUpdatePermissions(SchoolTeacherPermissionBoolean.CanAccessNonSchoolLibraries),
      ],
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
    })
  }

  async function updateDefaultPrivacy(
    wallPrivacy: WallPrivacyOption,
    initialDefaultPrivacy: WallPrivacyOption,
  ): Promise<void> {
    const libraryId = library.value?.id as number
    try {
      const response = await LibraryPermissionsApi.update(libraryId, {
        teacher: { defaultWallPrivacy: wallPrivacy },
      })
      const permissions = (response.data as JsonAPIResource<SchoolLibraryPermission>)?.attributes.settings?.teacher
      if (permissions != null) setPermissionsAndDefaultPrivacyState(permissions)
    } catch (e) {
      queue.clear()
      void globalSnackbarStore.setSnackbar({
        message: __('Error updating default privacy.'),
        notificationType: SnackbarNotificationType.error,
      })
      defaultPrivacy.value = initialDefaultPrivacy
      captureFetchException(e, { source: 'SettingsSchoolPermissionsTeachersUpdateDefaultPrivacy' })
    }
  }

  async function queueUpdateDefaultPrivacy(wallPrivacy: WallPrivacyOption): Promise<void> {
    const initialDefaultPrivacy = defaultPrivacy.value
    defaultPrivacy.value = wallPrivacy
    if (wallPrivacy === WallPrivacyOption.Private) {
      permissionsState.defaultWallVisitorPermission = WallPermission.Read
    }
    void queue.enqueue(
      'updateTeacherDefaultPrivacy',
      async () => await updateDefaultPrivacy(wallPrivacy, initialDefaultPrivacy),
    )
  }

  async function updateDefaultWallPermission(
    permission: WallPermission,
    initialPermission: WallPermission,
  ): Promise<void> {
    const libraryId = library.value?.id as number
    try {
      const response = await LibraryPermissionsApi.update(libraryId, {
        teacher: { defaultWallVisitorPermission: permission },
      })
      const permissions = (response.data as JsonAPIResource<SchoolLibraryPermission>)?.attributes.settings?.teacher
      if (permissions != null) {
        setPermissionsAndDefaultPrivacyState(permissions)
      }
    } catch (e) {
      void globalSnackbarStore.setSnackbar({
        message: __('Error updating permission.'),
        notificationType: SnackbarNotificationType.error,
      })
      queue.clear()
      permissionsState.defaultWallVisitorPermission = initialPermission
      captureFetchException(e, { source: 'SettingsSchoolPermissionsTeachersUpdateDefaultWallPermission' })
    }
  }

  async function queueUpdateWallDefaultPermission(wallPermission: WallPermission): Promise<void> {
    const initialDefaultWallPermission = permissionsState.defaultWallVisitorPermission as WallPermission
    permissionsState.defaultWallVisitorPermission = wallPermission
    void queue.enqueue(
      'updateTeacherDefaultWallPermission',
      async () => await updateDefaultWallPermission(wallPermission, initialDefaultWallPermission),
    )
  }

  async function updateV2AccessSetting(
    key: SchoolPermissionString,
    newSetting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
    initialSetting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  ): Promise<void> {
    const libraryId = library.value?.id as number
    try {
      const response = await LibraryPermissionsApi.update(libraryId, {
        teacher: { [key]: newSetting },
      })
      const permissions = (response.data as JsonAPIResource<SchoolLibraryPermission>)?.attributes.settings?.teacher
      if (permissions != null) {
        setPermissionsAndDefaultPrivacyState(permissions)
      }
    } catch (e) {
      void globalSnackbarStore.setSnackbar({
        message: __('Error updating permission.'),
        notificationType: SnackbarNotificationType.error,
      })
      queue.clear()
      permissionsState[key] = initialSetting
      captureFetchException(e, { source: 'SettingsSchoolPermissionsTeachersUpdateV2AccessSetting' })
    }
  }

  async function queueUpdateV2AccessSetting(
    key: SchoolPermissionString,
    setting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  ): Promise<void> {
    const initialSetting = permissionsState[key] as WallAccessRule | WallAccessDefault | AllowedDefaultWallRole

    // Check if this permission change will trigger retroactive wall updates
    if (willTriggerRetroactiveWallUpdates(key, setting)) {
      showRetroactiveWallUpdatesConfirmationV2(key, setting, initialSetting)
      return
    }

    // Proceed with the update directly if no retroactive updates needed
    proceedWithV2PermissionUpdate(key, setting, initialSetting)
  }

  function willTriggerRetroactiveWallUpdates(
    key: SchoolPermissionString,
    setting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  ): boolean {
    // These are the access control settings V2 permissions that trigger retroactive updates
    const accessControlV2Permissions = [
      SchoolPermissionString.AllowVisitorAccess,
      SchoolPermissionString.AllowStudentAccess,
      SchoolPermissionString.AllowTeacherAccess,
      SchoolPermissionString.ListedSetting,
      SchoolPermissionString.PublishableSetting,
      SchoolPermissionString.RequirePassword,
      SchoolPermissionString.RequireLogin,
    ]

    return willTriggerRetroactiveWallUpdatesV2(key, setting, accessControlV2Permissions)
  }

  function showRetroactiveWallUpdatesConfirmationV2(
    key: SchoolPermissionString,
    setting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
    initialSetting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  ): void {
    const dialogConfig = createRetroactiveWallUpdatesConfirmationDialog({
      userType: 'teacher',
      isPhone: useWindowSizeStore().isPhone,
      afterConfirmAction: () => proceedWithV2PermissionUpdate(key, setting, initialSetting),
      permissionKey: key,
      settingValue: setting,
    })
    globalConfirmationDialogStore.openConfirmationDialog(dialogConfig)
  }

  function proceedWithV2PermissionUpdate(
    key: SchoolPermissionString,
    setting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
    initialSetting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  ): void {
    permissionsState[key] = setting
    void queue.enqueue(
      'updateTeacherV2AccessSetting',
      async () => await updateV2AccessSetting(key, setting, initialSetting),
    )
  }

  return {
    // State
    permissionsState,
    library,
    defaultPrivacy,
    // Getters
    isSchoolLibrary,
    isSchoolPermissionsTeachersLoading,
    disabledTeacherDefaultPrivacy,
    mostSecureEnabledTeacherPrivacyOption,
    isMakeAPadletForTeachersDisabled,
    areAllTeacherPrivacyOptionsDisabled,

    // Actions
    initialize,
    queueUpdatePermissions,
    queueUpdateDefaultPrivacy,
    queueUpdateWallDefaultPermission,
    queueUpdateV2AccessSetting,
    confirmDisableCanAccessNonSchoolLibraries,
  }
})
export { useSettingsSchoolPermissionsTeachersStore }
